import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboard, usePhotos, useProject, useTasks } from '@/api/hooks';
import { Icon, type IconName } from '@/design-system/icon/Icon';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { toast } from '@/design-system/primitives/Toast/Toast';
import { MeerwerkCreateDialog } from '@/features/budget/MeerwerkDialogs';
import { DecisionCreateDialog } from '@/features/decisions/DecisionDialogs';
import { PhotoUploadDialog } from '@/features/werf/PhotoDialogs';
import styles from './WerfModus.module.css';

// F15 — quick-action buttons in the mobile werf-modus. The phaseId is
// hard-coded to the current "Ruw" phase for the v1 mobile flow; the WerfModus
// screen exists to capture artefacts mid-werf without picking a phase. The
// reused dialogs all accept a defaultPhaseId so we surface the right one.
const CURRENT_PHASE = 'Ruw' as const;

// Format today's date as "dd mmm" in Dutch (e.g. "17 mei"). Used in the
// "Vandaag op de werf" section header.
function todayShort(): string {
  return new Date().toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' });
}

type QuickActionId = 'photo' | 'voice' | 'meerwerk' | 'decision';

interface QuickActionProps {
  id: QuickActionId;
  icon: IconName;
  title: string;
  sub: string;
  big?: boolean;
  primary?: boolean;
  onClick: () => void;
}

function QuickAction({ id, icon, title, sub, big, primary, onClick }: QuickActionProps) {
  return (
    <button
      type="button"
      data-action={id}
      className={`${styles.quick} ${big ? styles.quickBig : ''} ${
        primary ? styles.quickPrimary : ''
      }`}
      onClick={onClick}
    >
      <Icon
        name={icon}
        size={26}
        color={primary ? 'var(--paper)' : 'var(--accent)'}
        strokeWidth={1.4}
      />
      <div className={styles.quickTitle}>{title}</div>
      <div className={styles.quickSub}>{sub}</div>
    </button>
  );
}

interface MobItemProps {
  icon: IconName;
  title: string;
  sub: string;
  kind: 'warn' | 'bad' | 'info';
}

function MobItem({ icon, title, sub, kind }: MobItemProps) {
  return (
    <div className={`row gap-10 ${styles.item}`}>
      <div
        className={`${styles.itemIcon}`}
        style={{
          background: `var(--${kind}-tint)`,
          color: kind === 'warn' ? 'oklch(48% 0.13 50)' : `var(--${kind})`,
        }}
      >
        <Icon name={icon} size={15} />
      </div>
      <div className={`flex-1 ${styles.itemBody}`}>
        <div className={styles.itemTitle}>{title}</div>
        <div className={styles.itemSub}>{sub}</div>
      </div>
      <Icon name="chevR" size={14} color="var(--ink-4)" />
    </div>
  );
}

interface TabItem {
  id: string;
  icon: IconName | null;
  label: string;
  to?: string;
  badge?: number;
}

export function WerfModus() {
  const [photoOpen, setPhotoOpen] = useState(false);
  const [meerwerkOpen, setMeerwerkOpen] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);

  // 2026-05-17 — header title + phase callout + today's list + photo strip
  // all come from the API now. Previously the body was static Phase-1
  // fixture data ("Verdiep 1 + welfsels · 62%", "Werfvergadering 09:00",
  // "foto 1 / foto 2 / foto 3 / foto 4") regardless of project state.
  const project = useProject();
  const dashboard = useDashboard();
  const sitePart = project.data?.name?.trim() || 'Mijn project';
  const currentPhase = dashboard.data?.currentPhase;
  const phasePart = currentPhase?.label;
  const werfTitle = phasePart ? `${sitePart} · ${phasePart}` : sitePart;

  // Today's tasks → "Vandaag op de werf" list. Pull open tasks and filter
  // client-side to those due today (the API has no native date filter).
  const today = new Date().toISOString().slice(0, 10);
  const openTasks = useTasks({ status: 'Open', pageSize: 50 });
  const todaysTasks = (openTasks.data?.items ?? []).filter((t) => t.due === today).slice(0, 4);

  // Latest 4 photos → "Vandaag vastgelegd" strip.
  const recentPhotos = usePhotos({ pageSize: 4 });
  const photoTotal = Number(recentPhotos.data?.total ?? 0) || 0;

  const tabs: TabItem[] = [
    { id: 'werf', icon: 'home', label: 'Werf', to: '/werf-modus' },
    { id: 'fases', icon: 'timeline', label: 'Fases', to: '/fases/ruw' },
    { id: 'spacer', icon: null, label: '' },
    { id: 'docs', icon: 'folder', label: 'Docs', to: '/documenten' },
  ];

  // Phase progress arrives as decimal-or-string from the codegen.
  const phaseProgress =
    typeof currentPhase?.progress === 'number'
      ? currentPhase.progress
      : typeof currentPhase?.progress === 'string'
        ? Number(currentPhase.progress) || 0
        : 0;
  const phaseProgressPct = Math.round(phaseProgress * 100);

  const trigger = (action: QuickActionId) => {
    setFabOpen(false);
    switch (action) {
      case 'photo':
        setPhotoOpen(true);
        return;
      case 'voice':
        // No backend endpoint for voice notes — Phase 8 will land transcription.
        toast.info('Spraaknotitie komt later (Phase 8). Maak voorlopig een foto + caption.');
        return;
      case 'meerwerk':
        setMeerwerkOpen(true);
        return;
      case 'decision':
        setDecisionOpen(true);
        return;
    }
  };

  const QUICK_ACTIONS: ReadonlyArray<{
    id: QuickActionId;
    icon: IconName;
    title: string;
    sub: string;
    primary?: boolean;
    big?: boolean;
  }> = [
    { id: 'photo', icon: 'camera', title: 'Foto', sub: 'op het plan', primary: true, big: true },
    { id: 'voice', icon: 'mic', title: 'Spraaknotitie', sub: 'auto-transcriptie' },
    { id: 'meerwerk', icon: 'receipt', title: 'Meerwerk', sub: 'vastleggen + ondertekenen' },
    { id: 'decision', icon: 'decision', title: 'Beslissing', sub: 'met motivatie' },
  ];

  return (
    <div className={`bv ${styles.root}`}>
      <div className={`row ${styles.statusBar}`}>
        <span className="mono">09:42</span>
        <div className={`ml-auto row gap-4 ${styles.statusRight}`}>
          <span className={`mono ${styles.statusNet}`}>4G</span>
          <span className={styles.battery}>
            <span className={styles.batteryFill} />
          </span>
        </div>
      </div>

      <div className={`row gap-10 ${styles.topbar}`}>
        <div className={styles.logo}>b</div>
        <div>
          <div className={styles.werfTag}>WERF-MODUS</div>
          <div className={styles.werfTitle}>{werfTitle}</div>
        </div>
        <div className="ml-auto">
          <Pill kind="ok" dot={false}>
            ● online
          </Pill>
        </div>
      </div>

      <div className={styles.section}>
        <div className={`eyebrow ${styles.sectionLabel}`}>Snel vastleggen</div>
        <div className={styles.quickGrid}>
          {QUICK_ACTIONS.map((a) => (
            <QuickAction
              key={a.id}
              id={a.id}
              icon={a.icon}
              title={a.title}
              sub={a.sub}
              big={a.big}
              primary={a.primary}
              onClick={() => trigger(a.id)}
            />
          ))}
        </div>
      </div>

      {currentPhase && (
        <div className={styles.phaseCallout}>
          <div className={`row gap-6 ${styles.calloutHead}`}>
            <Pill kind="warn">Bezig</Pill>
            <span className={styles.calloutTitle}>{currentPhase.label}</span>
          </div>
          <div className={styles.calloutBar}>
            <span style={{ width: `${phaseProgressPct}%`, background: 'var(--warn)' }} />
          </div>
          <div className={`row ${styles.calloutMeta}`}>
            <span>{phaseProgressPct}% voltooid</span>
            {currentPhase.end && (
              <span className="ml-auto mono">tot {String(currentPhase.end).slice(5)}</span>
            )}
          </div>
        </div>
      )}

      <div className={styles.section}>
        <div className={`row ${styles.sectionHead}`}>
          <span className="eyebrow">Vandaag op de werf</span>
          <span className={`ml-auto mono ${styles.sectionDate}`}>{todayShort()}</span>
        </div>
        {todaysTasks.length === 0 ? (
          <div className="mono c-ink-3">Geen taken voor vandaag.</div>
        ) : (
          <div className="col gap-8">
            {todaysTasks.map((t) => (
              <MobItem
                key={t.id}
                icon={t.urgent ? 'flag' : 'checkbox'}
                title={t.title}
                sub={t.assignedTo || (t.phaseId ? `fase ${t.phaseId}` : '')}
                kind={t.urgent ? 'bad' : 'info'}
              />
            ))}
          </div>
        )}
      </div>

      <div className={`${styles.section} ${styles.bottomPad}`}>
        <div className={`row ${styles.sectionHead}`}>
          <span className="eyebrow">Recent vastgelegd</span>
          <span className={`ml-auto mono ${styles.sectionDate}`}>
            {photoTotal} {photoTotal === 1 ? 'foto' : 'foto’s'}
          </span>
        </div>
        {photoTotal === 0 ? (
          <div className="mono c-ink-3">
            Nog geen foto's. Tik op "Foto" om de eerste vast te leggen.
          </div>
        ) : (
          <div className={`row gap-6 ${styles.photoStrip}`}>
            {(recentPhotos.data?.items ?? []).slice(0, 4).map((p) => (
              <div key={p.id} className={`ph ${styles.photoTile}`} title={p.caption ?? ''}>
                {p.caption ?? 'foto'}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.fab}
        aria-label="Nieuw item"
        onClick={() => setFabOpen(true)}
      >
        <Icon name="plus" size={28} strokeWidth={2.2} />
      </button>

      {fabOpen && (
        <>
          <button
            type="button"
            aria-label="Sluiten"
            className={styles.fabBackdrop}
            onClick={() => setFabOpen(false)}
          />
          <div className={styles.fabSheet} role="menu" aria-label="Snelacties">
            <div className={`eyebrow ${styles.fabSheetTitle}`}>Snelle actie</div>
            {QUICK_ACTIONS.map((a) => (
              <button
                key={a.id}
                type="button"
                role="menuitem"
                data-action={a.id}
                className={`row gap-12 ${styles.fabItem}`}
                onClick={() => trigger(a.id)}
              >
                <Icon name={a.icon} size={18} color="var(--accent)" />
                <div className="flex-1">
                  <div className={styles.fabItemTitle}>{a.title}</div>
                  <div className={styles.fabItemSub}>{a.sub}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div className={`row ${styles.tabbar}`}>
        {tabs.map((t) => {
          if (!t.icon) {
            return <div key={t.id} className={`col ${styles.tab} ${styles.tabHidden}`} />;
          }
          const active = t.to === '/werf-modus';
          return (
            <Link
              key={t.id}
              to={t.to ?? '/'}
              className={`col ${styles.tab}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon name={t.icon} size={18} color={active ? 'var(--ink)' : 'var(--ink-3)'} />
              <span className={`${styles.tabLabel} ${active ? styles.tabLabelActive : ''}`}>
                {t.label}
              </span>
              {t.badge && <span className={styles.tabBadge}>{t.badge}</span>}
            </Link>
          );
        })}
      </div>

      <PhotoUploadDialog
        open={photoOpen}
        onOpenChange={setPhotoOpen}
        defaultPhaseId={CURRENT_PHASE}
      />
      <MeerwerkCreateDialog
        open={meerwerkOpen}
        onOpenChange={setMeerwerkOpen}
        defaultPhaseId={CURRENT_PHASE}
      />
      <DecisionCreateDialog
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        defaultPhaseId={CURRENT_PHASE}
      />
    </div>
  );
}
